import '@rainbow-me/rainbowkit/styles.css'
import { getDefaultConfig } from '@rainbow-me/rainbowkit'
import { http, fallback } from 'wagmi'

// WalletConnect / Reown project ID.
// NOTE:
// - In production this MUST be set in env (Vercel, etc.) for WalletConnect to work.
// - In local/dev we don't hard-throw so the app can still load without the key
//   (WalletConnect will simply fail to connect).
const envProjectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim?.() || ''
const isProd = import.meta.env.MODE === 'production'

if (!envProjectId) {
  const msg =
    'VITE_WALLETCONNECT_PROJECT_ID is not set. WalletConnect-based wallets will not work until you configure a Project ID from https://cloud.reown.com.'
  if (isProd) {
    console.error(msg)
  } else {
    console.warn(msg)
  }
}

// Always pass *some* string to RainbowKit so the module can initialize.
// When envProjectId is empty this will be a dummy ID; calls to Reown will fail,
// but the rest of the app (and injected wallets) can still function.
const walletConnectProjectId = envProjectId || 'demo-project-id'

// ─── RPC Configuration ───
// Priority: env-configured dedicated RPC > public endpoints
// Set VITE_RPC_URL_MAINNET / VITE_RPC_URL_TESTNET in .env for dedicated RPC (recommended for 200+ users)
const mainnetRpcUrls = [
  import.meta.env.VITE_RPC_URL_MAINNET,   // Dedicated RPC (if set)
  'https://rpc.monad.xyz',                  // Primary public
  'https://monad-mainnet.public.blastapi.io', // Fallback public
].filter(Boolean)

const testnetRpcUrls = [
  import.meta.env.VITE_RPC_URL_TESTNET,   // Dedicated RPC (if set)
  'https://testnet-rpc.monad.xyz',          // Primary public
  'https://monad-testnet.public.blastapi.io', // Fallback public
].filter(Boolean)

// Build wagmi fallback transport — tries each RPC in order, auto-failover on error
function buildTransport(urls) {
  if (urls.length === 1) return http(urls[0])
  return fallback(urls.map(url => http(url, {
    timeout: 10_000,      // 10s timeout per request
    retryCount: 1,        // 1 retry before moving to next RPC
    retryDelay: 500,      // 500ms between retries
  })))
}

// Monad Testnet – supports MetaMask, Haha Wallet, WalletConnect, and any injected wallet
export const monadTestnet = {
  id: 10143,
  name: 'Monad Testnet',
  nativeCurrency: { name: 'MON', symbol: 'MON', decimals: 18 },
  rpcUrls: {
    default: { http: testnetRpcUrls },
    public: { http: testnetRpcUrls },
  },
  blockExplorers: {
    default: { name: 'Monad Explorer', url: 'https://testnet-explorer.monad.xyz' },
  },
  testnet: true,
}

// Monad Mainnet – official network info: https://docs.monad.xyz/developer-essentials/network-information
export const monadMainnet = {
  id: 143,
  name: 'Monad Mainnet',
  nativeCurrency: { name: 'MON', symbol: 'MON', decimals: 18 },
  rpcUrls: {
    default: { http: mainnetRpcUrls },
    public: { http: mainnetRpcUrls },
  },
  blockExplorers: {
    default: { name: 'MonadVision', url: 'https://monadvision.com' },
  },
  testnet: false,
}

// getDefaultConfig includes: Injected (MetaMask, Haha, etc.), WalletConnect, and other connectors
export const rainbowConfig = getDefaultConfig({
  appName: 'Last Nad Standing',
  projectId: walletConnectProjectId,
  chains: [monadTestnet, monadMainnet],
  transports: {
    [monadTestnet.id]: buildTransport(testnetRpcUrls),
    [monadMainnet.id]: buildTransport(mainnetRpcUrls),
  },
  ssr: false,
})
